/**
 * Phase B — Liste transverse des tâches.
 *
 * Fetch parallèle `/tasks` + `/leads` pour afficher le nom du projet rattaché
 * (le backend ne renvoie que `lead_id` sur Task). Filtre par statut, checkbox
 * "cocher comme fait" avec update optimiste + rollback. Loading/error/empty
 * soignés, thème light + dark via variables CSS `platform-*`.
 *
 * Champs affichés : checkbox statut, titre, projet rattaché (lien), échéance,
 * statut. Pas d'assignation ni tri avancé — déportés à un B2 dédié.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  CheckSquare,
  Square,
  ArrowRight,
  AlertTriangle,
  RefreshCw,
  CalendarClock,
  ClipboardCheck,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "En cours", cls: "badge-warning" },
  done: { label: "Fait", cls: "badge-success" },
  cancelled: { label: "Annulé", cls: "badge-neutral" },
};
const STATUS_ORDER = ["pending", "done", "cancelled"];

function formatDue(v) {
  if (!v) return { text: "Sans échéance", isOverdue: false };
  try {
    const d = new Date(v);
    const now = new Date();
    const isOverdue = d < now;
    return {
      text: d.toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" }),
      isOverdue,
    };
  } catch {
    return { text: "—", isOverdue: false };
  }
}

export default function TasksListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [rdvsToDebrief, setRdvsToDebrief] = useState([]);
  const [leadsById, setLeadsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Phase D2 — Fetch parallèle : tasks + leads + RDV à débriefer
      const [tasksRes, leadsRes, rdvsRes] = await Promise.all([
        api.get("/tasks"),
        api.get("/leads", { params: { page_size: 500 } }),
        api.get("/rendezvous", { params: { needs_debrief: true } }),
      ]);
      setItems(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      setRdvsToDebrief(Array.isArray(rdvsRes.data) ? rdvsRes.data : []);
      const map = {};
      for (const l of leadsRes.data.items || []) {
        map[l.id] = l;
      }
      setLeadsById(map);
    } catch (err) {
      const msg = formatApiError(err, "Impossible de charger les tâches");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "__all__") {
      list = list.filter((t) => t.status === statusFilter);
    }
    // Tri stable : à faire (par échéance croissante) → puis fait → annulé
    return [...list].sort((a, b) => {
      if (a.status !== b.status) return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
      const da = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const db = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      return da - db;
    });
  }, [items, statusFilter]);

  const countsByStatus = useMemo(() => {
    const c = {};
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items]);

  const projectNameFor = (leadId) => {
    const l = leadsById[leadId];
    if (!l) return "—";
    const full = `${l.first_name || ""} ${l.last_name || ""}`.trim();
    return full || "Projet";
  };

  const toggleDone = async (task) => {
    const nextStatus = task.status === "done" ? "pending" : "done";
    // Optimistic
    setItems((arr) => arr.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
    try {
      if (nextStatus === "done") {
        await api.post(`/tasks/${task.id}/complete`);
      } else {
        await api.patch(`/tasks/${task.id}`, { status: "pending" });
      }
    } catch (err) {
      // Rollback
      setItems((arr) => arr.map((t) => (t.id === task.id ? { ...t, status: task.status } : t)));
      toast.error(formatApiError(err, "Impossible de mettre à jour la tâche"));
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-list-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold text-platform-heading">
            <CheckSquare className="h-5 w-5 text-[#C4553B]" /> Tâches
          </h1>
          <p className="text-sm text-platform-body">
            Toutes les tâches, tous projets confondus. Cochez-les pour les marquer comme faites.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-platform-border bg-platform-surface text-platform-body"
          data-testid="tasks-refresh"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </Button>
      </div>

      {/* Phase D2 — Section RDV à débriefer (au-dessus des tâches) */}
      {rdvsToDebrief.length > 0 && (
        <Card className="border-warning/60 bg-warning-subtle/15" data-testid="rdv-to-debrief-section">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b border-warning/40 px-4 py-3">
              <ClipboardCheck className="h-4 w-4 text-warning-text" />
              <span className="text-sm font-semibold text-platform-heading">
                RDV à débriefer
              </span>
              <Badge className="badge-warning ml-1 border text-[10px]">
                {rdvsToDebrief.length}
              </Badge>
            </div>
            <ul className="divide-y divide-platform-border/60">
              {rdvsToDebrief.map((r) => {
                const lead = leadsById[r.lead_id];
                const leadName = lead
                  ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "Projet"
                  : "Projet";
                const dt = r.scheduled_at
                  ? new Date(r.scheduled_at).toLocaleString("fr-BE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  : "—";
                return (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-platform-hover"
                    data-testid={`rdv-debrief-row-${r.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-platform-heading">
                        {leadName} · <span className="text-platform-muted">{r.contact_name || "—"}</span>
                      </div>
                      <div className="text-xs text-platform-muted">
                        <CalendarClock className="inline h-3 w-3 mr-1" />
                        {dt} · {r.location || "lieu non précisé"}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/projects/${r.lead_id}#rdv-${r.id}`)}
                      className="bg-[#C4553B] text-white hover:bg-[#A94531]"
                      data-testid={`rdv-debrief-open-${r.id}`}
                    >
                      Débriefer <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card className="border-platform-border bg-platform-surface">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <label className="text-xs uppercase tracking-wide text-platform-muted">Statut</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[220px] border-platform-border bg-platform-surfaceAlt text-platform-heading"
              data-testid="tasks-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toutes ({items.length})</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_META[s].label} ({countsByStatus[s] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-platform-muted" data-testid="tasks-count">
            {loading ? "…" : `${filtered.length} affichée${filtered.length > 1 ? "s" : ""}`}
          </span>
        </CardContent>
      </Card>

      {/* Corps */}
      <Card className="border-platform-border bg-platform-surface">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-14 text-sm text-platform-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement des tâches…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-14 text-center" data-testid="tasks-error">
              <AlertTriangle className="h-8 w-8 text-[#D99A2B]" />
              <div className="text-sm text-platform-body">{error}</div>
              <Button variant="outline" size="sm" onClick={load} className="border-platform-border">
                Réessayer
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="tasks-empty">
              <div className="rounded-full bg-[#C4553B]/10 p-3">
                <CheckSquare className="h-8 w-8 text-[#C4553B]" />
              </div>
              <div className="max-w-md text-sm text-platform-body">
                {statusFilter === "pending" ? (
                  <>
                    Rien à faire pour l&apos;instant. Les tâches créées sur un projet apparaîtront ici,
                    triées par échéance.
                  </>
                ) : statusFilter === "__all__" ? (
                  <>Aucune tâche pour le moment. Créez-en depuis la fiche d&apos;un projet.</>
                ) : (
                  <>Aucune tâche avec le statut “{STATUS_META[statusFilter].label}”.</>
                )}
              </div>
              {statusFilter !== "pending" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStatusFilter("pending")}
                  className="border-platform-border"
                >
                  Voir les tâches à faire
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-platform-border/60">
              {filtered.map((t) => {
                const isDone = t.status === "done";
                const meta = STATUS_META[t.status] || STATUS_META.pending;
                const due = formatDue(t.due_at);
                return (
                  <li
                    key={t.id}
                    className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-platform-hover"
                    data-testid={`tasks-row-${t.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleDone(t)}
                      disabled={t.status === "cancelled"}
                      className={`mt-0.5 shrink-0 rounded p-1 transition-colors ${
                        t.status === "cancelled"
                          ? "cursor-not-allowed text-platform-muted"
                          : isDone
                            ? "text-emerald-500 hover:bg-emerald-500/10"
                            : "text-platform-muted hover:bg-platform-hover hover:text-emerald-500"
                      }`}
                      title={isDone ? "Marquer comme non fait" : "Marquer comme fait"}
                      data-testid={`tasks-toggle-${t.id}`}
                    >
                      {isDone ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-sm ${
                          isDone ? "text-platform-muted line-through" : "text-platform-heading"
                        }`}
                      >
                        {t.title || "(sans titre)"}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-platform-muted">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            due.isOverdue && !isDone && t.status !== "cancelled"
                              ? "text-[#E9A98F]"
                              : ""
                          }`}
                        >
                          <CalendarClock className="h-3 w-3" /> {due.text}
                          {due.isOverdue && !isDone && t.status !== "cancelled" && (
                            <span className="badge-error ml-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase">
                              en retard
                            </span>
                          )}
                        </span>
                        {t.lead_id && (
                          <Link
                            to={`/projects/${t.lead_id}`}
                            className="inline-flex items-center gap-1 text-[#C4553B] hover:text-[#E07A62]"
                            data-testid={`tasks-project-${t.id}`}
                          >
                            {projectNameFor(t.lead_id)} <ArrowRight className="h-3 w-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                    <Badge className={`border ${meta.cls} shrink-0`}>{meta.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
